/* Soolla Warp Hero — production engine (vanilla JS, zero dependencies)
   ====================================================================
   This is a 3D FLY-THROUGH, not a falling animation. Pouches spawn far
   away in a perspective-projected field, rush TOWARD the camera (growing
   as they approach), and exit off the edges before respawning deep.

   DO NOT REIMPLEMENT THIS EFFECT. Use this file verbatim.

   Usage:
     var warp = SoollaWarp.mount(containerEl, {
       images: [...],   // required: array of image URLs (transparent PNGs)
       speed: 0.9,      // approved production value
       count: 56,       // approved production value
       size:  1.45,     // approved production value
       blur:  true      // approved production value (motion-blur ghosts)
     });
     warp.update({ speed: 1.2 });  // live-retune any option
     warp.destroy();               // stop + remove canvas

   The container must be position:relative (or absolute/fixed) — the
   canvas is injected as an absolutely-positioned, full-bleed child.
   Respects prefers-reduced-motion: renders a static scattered field. */

(function () {
  'use strict';

  var Z_NEAR = 0.055;   // kill plane — pouch has "passed" the camera
  var Z_FAR  = 1.0;     // spawn depth
  var FADE_Z = 0.16;    // begin fly-by fade below this depth

  function rand(a, b) { return a + Math.random() * (b - a); }

  function mount(container, options) {
    var opts = {
      speed: 0.9,
      count: 56,
      size: 1.45,
      blur: true,
      images: []
    };
    var key;
    for (key in options) { if (Object.prototype.hasOwnProperty.call(options, key)) opts[key] = options[key]; }
    if (!opts.images.length) { throw new Error('SoollaWarp.mount: options.images is required'); }

    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;pointer-events:none;';
    container.appendChild(canvas);
    var ctx = canvas.getContext('2d');

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var particles = [];
    var images = [];
    var raf = 0;
    var w = 0, h = 0, dpr = 1;
    var destroyed = false;

    function loadImages(list) {
      images = list.map(function (src) {
        var img = new Image();
        img.src = src;
        return img;
      });
    }
    loadImages(opts.images);

    function fov() { return Math.min(w, h) * 1.05; }

    function spawn(p, initial) {
      // Pick a screen-uniform position at spawn depth, then back-project to
      // world space — spreads pouches across the WHOLE viewport at every
      // depth. No center-clump, no starburst, and definitely no raining.
      p.z = initial ? rand(0.3, Z_FAR) : rand(0.88, Z_FAR);
      var sx = rand(-0.55, 0.55) * w;
      var sy = rand(-0.55, 0.55) * h;
      var f = fov();
      p.x = sx * p.z / f;                  // world units
      p.y = sy * p.z / f;
      p.vz = rand(0.16, 0.44);             // forward speed (depth/sec) — toward camera
      p.vx = rand(-0.035, 0.035);          // slight lateral drift only
      p.vy = rand(-0.03, 0.03);
      p.size = rand(0.028, 0.06);          // world size
      p.rot = rand(-0.5, 0.5);
      p.spin = rand(-0.55, 0.55);          // rad/sec
      p.img = images[(Math.random() * images.length) | 0] || null;
      p.delay = initial ? rand(0, 1.2) : rand(0.1, 1.1);
      p.age = 0;
      p.psx = null; p.psy = null;          // previous screen pos (for blur ghosts)
    }

    function ensureCount(n) {
      while (particles.length < n) {
        var p = {};
        spawn(p, true);
        particles.push(p);
      }
      if (particles.length > n) particles.length = n;
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    resize();
    ensureCount(opts.count);
    window.addEventListener('resize', resize);

    function drawPouch(p, x, y, pw, ph, a) {
      ctx.save();
      ctx.globalAlpha = Math.min(a, 1);
      ctx.translate(x, y);
      ctx.rotate(p.rot);
      ctx.drawImage(p.img, -pw / 2, -ph / 2, pw, ph);
      ctx.restore();
    }

    function paint(dt, animate) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      var f = fov();
      var cx = w / 2, cy = h / 2;
      var margin = 1.4;

      // far-to-near paint order so close pouches overlap distant ones
      var order = particles.slice().sort(function (a, b) { return b.z - a.z; });

      for (var i = 0; i < order.length; i++) {
        var p = order[i];
        if (animate) {
          if (p.delay > 0) { p.delay -= dt; continue; }
          p.age += dt;
          p.z -= p.vz * opts.speed * dt;   // depth DECREASES → flies toward viewer
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          p.rot += p.spin * dt;
        }

        var scale = f / p.z;               // perspective projection
        var sx = cx + p.x * scale;
        var sy = cy + p.y * scale;
        var px = p.size * scale * opts.size;   // on-screen width grows as z shrinks

        // exit: passed the camera, or fully off any edge / corner
        if (p.z <= Z_NEAR ||
            sx < -px * margin || sx > w + px * margin ||
            sy < -px * margin || sy > h + px * margin) {
          spawn(p, false);
          continue;
        }
        if (!p.img || !p.img.complete || !p.img.naturalWidth) continue;

        // opacity: ease in from a speck, hold, fade on the fly-by
        var fadeIn = animate ? Math.min(p.age / 0.45, 1) : 1;
        var fadeOut = p.z < FADE_Z
          ? Math.max((p.z - Z_NEAR) / (FADE_Z - Z_NEAR), 0)
          : 1;
        var depthIn = 0.35 + 0.65 * Math.min((Z_FAR - p.z) / 0.25, 1);
        var alpha = fadeIn * fadeOut * depthIn;
        if (alpha <= 0.01) continue;

        var ph = px * (p.img.naturalHeight / p.img.naturalWidth);

        // motion blur: ghost copies trailing along the screen velocity
        if (animate && opts.blur && p.psx !== null) {
          var dx = sx - p.psx, dy = sy - p.psy;
          var dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 6) {
            var ghosts = dist > 26 ? 3 : 2;
            for (var g = ghosts; g >= 1; g--) {
              var t = g / (ghosts + 1);
              drawPouch(p, sx - dx * t * 1.6, sy - dy * t * 1.6, px, ph,
                        alpha * 0.16 / g);
            }
          }
        }
        drawPouch(p, sx, sy, px, ph, alpha);
        p.psx = sx; p.psy = sy;
      }
    }

    var last = performance.now();
    function frame(now) {
      if (destroyed) return;
      raf = requestAnimationFrame(frame);
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      ensureCount(opts.count);
      paint(dt, true);
    }

    if (reduced) {
      // static field: wait for images, then paint once with no motion
      var tries = 0;
      var settle = setInterval(function () {
        tries += 1;
        var ready = images.every(function (im) { return im.complete; });
        if (ready || tries > 40) {
          clearInterval(settle);
          if (!destroyed) paint(0, false);
        }
      }, 100);
    } else {
      raf = requestAnimationFrame(frame);
    }

    return {
      update: function (partial) {
        var k;
        for (k in partial) { if (Object.prototype.hasOwnProperty.call(partial, k)) opts[k] = partial[k]; }
        if (partial && partial.images) loadImages(partial.images);
      },
      destroy: function () {
        destroyed = true;
        cancelAnimationFrame(raf);
        window.removeEventListener('resize', resize);
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    };
  }

  window.SoollaWarp = { mount: mount };
})();
